"use client";

import CareersOpenPositionsSkeleton from "@/components/careers/CareersOpenPositionsSkeleton";
import JobCard from "@/components/careers/JobCard";
import JobEmptyState from "@/components/careers/JobEmptyState";
import JobFilterTabs, {
  type JobFilterValue,
} from "@/components/careers/JobFilterTabs";
import Wrapper from "@/components/common/Wrapper";
import Button from "@/components/library/Button";
import { getLocaleFromPathname } from "@/hooks/useLocalePath";
import { BASE_URL } from "@/utils/baseUrl";
import type { JobListing } from "@/utils/jobsPublic";
import { isJobType, jobDetailPath, jobTypeLabel, MAX_JOBS_PER_VIEW } from "@/utils/jobsPublic";
import type { SupportedLanguage } from "@/utils/library/languages";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type CareersPageProps = {
  jobs: JobListing[];
  initialFilter?: JobFilterValue;
  isLoading?: boolean;
};

function filterJobs(
  jobs: JobListing[],
  filter: JobFilterValue,
  query: string,
): JobListing[] {
  let result = jobs;

  if (filter !== "all") {
    result = result.filter((j) => j.type === filter);
  }

  const q = query.trim().toLowerCase();
  if (q) {
    result = result.filter(
      (j) =>
        j.title.toLowerCase().includes(q) ||
        j.summary.toLowerCase().includes(q) ||
        (j.location?.toLowerCase().includes(q) ?? false) ||
        (j.departmentLabel?.toLowerCase().includes(q) ?? false),
    );
  }

  return result;
}

export default function CareersPage({
  jobs,
  initialFilter = "all",
  isLoading = false,
}: Readonly<CareersPageProps>) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const lang = getLocaleFromPathname(pathname) as SupportedLanguage;
  const pathFilter = pathname.split("/").findLast(Boolean);
  const routeFilter = pathFilter && isJobType(pathFilter) ? pathFilter : "all";

  const [filter, setFilter] = useState<JobFilterValue>(initialFilter);
  const [loadingFilter, setLoadingFilter] = useState<JobFilterValue | null>(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    key: "",
    count: MAX_JOBS_PER_VIEW,
  });

  const filtered = useMemo(
    () => filterJobs(jobs, filter, search),
    [jobs, filter, search],
  );
  const paginationKey = `${filter}:${search}:${jobs.length}`;
  const visibleCount =
    pagination.key === paginationKey ? pagination.count : MAX_JOBS_PER_VIEW;
  const visibleJobs = filtered.slice(0, visibleCount);
  const hasMoreJobs = visibleCount < filtered.length;
  const isRouteTransitionLoading =
    loadingFilter !== null && loadingFilter !== routeFilter;
  const showLoading = isLoading || isRouteTransitionLoading;

  const handleFilterChange = (nextFilter: JobFilterValue) => {
    if (nextFilter !== routeFilter) {
      setLoadingFilter(nextFilter);
    }
    setFilter(nextFilter);
  };

  const detailsHrefForJob = (job: JobListing) => jobDetailPath(job, lang);
  const applyHrefForJob = (job: JobListing) =>
    `${jobDetailPath(job, lang)}?apply=1`;
  const shareUrlForJob = (job: JobListing) =>
    `${BASE_URL}${jobDetailPath(job, lang)}`;

  const emptyVariant = filter === "all" ? "all" : "type";
  const emptyTypeLabel =
    filter === "all" ? undefined : jobTypeLabel(filter);
  let jobsContent: ReactNode;

  if (showLoading) {
    jobsContent = <CareersOpenPositionsSkeleton />;
  } else if (filtered.length === 0) {
    jobsContent = (
      <JobEmptyState variant={emptyVariant} typeLabel={emptyTypeLabel} />
    );
  } else {
    jobsContent = (
      <>
        <div
          className={clsx(
            "grid gap-6",
            "grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3",
          )}
        >
          {visibleJobs.map((job) => (
            <JobCard
              key={`${job.type}-${job.id}`}
              job={job}
              shareUrl={shareUrlForJob(job)}
              detailsHref={detailsHrefForJob(job)}
              applyHref={applyHrefForJob(job)}
            />
          ))}
        </div>
        {hasMoreJobs ? (
          <div className="mt-10 flex justify-center">
            <Button
              type="tertiary"
              className="border border-black/10 font-monument text-sm"
              onClick={() =>
                setPagination({
                  key: paginationKey,
                  count: visibleCount + MAX_JOBS_PER_VIEW,
                })
              }
            >
              {t("careers.showMore")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <Wrapper fullWidth className="w-full max-w-6xl px-4! py-12 md:py-16">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-monument tracking-tight text-[2rem] font-normal text-black md:text-[2.5rem]">
          {t("careers.openPositions")}
        </h2>
        <div className="relative w-full max-w-md">
          <svg
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/40"
            xmlns="http://www.w3.org/2000/svg"
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("careers.searchLabel")}
            placeholder={t("careers.searchPlaceholder")}
            className="w-full rounded-full py-3 pl-11 pr-4 focus:outline-none border border-black/10 bg-white font-monument text-sm text-black placeholder:text-black/40 focus:ring-1 focus:ring-black/30"
          />
        </div>
      </div>

      <div className="mt-8">
        <JobFilterTabs value={filter} lang={lang} onChange={handleFilterChange} />
      </div>

      <div className="mt-10">{jobsContent}</div>
    </Wrapper>
  );
}
